import React from "react";
import {
  AbsoluteFill,
  Audio,
  Composition,
  Easing,
  Img,
  interpolate,
  Sequence,
  staticFile,
  useCurrentFrame,
} from "remotion";
import {
  ArrowRight,
  BookOpen,
  Briefcase,
  CheckCircle2,
  Globe,
  GraduationCap,
  HelpCircle,
  QrCode,
  ShieldAlert,
  User,
  X,
  XCircle,
  Zap,
} from "lucide-react";

export const NixlevExplainerComposition: React.FC = () => {
  return (
    <Composition
      id="NixlevExplainer"
      component={NixlevExplainerVideo}
      durationInFrames={1395} // 46.50 seconds @ 30fps
      fps={30}
      width={1080}
      height={1920}
    />
  );
};

// Official Nixlev Palette
const BG_CREAM = "#F7F5F0";
const NIXLEV_GREEN = "#1B3B2B";
const NIXLEV_SAGE = "#EBF3EE";
const NIXLEV_GOLD = "#D4A346";
const TEXT_DARK = "#1F1E1D";
const TEXT_MUTED = "#6B6964";
const BORDER_COLOR = "#E2DDD5";

export const NixlevExplainerVideo: React.FC = () => {
  return (
    <AbsoluteFill
      style={{
        backgroundColor: BG_CREAM,
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', sans-serif",
        color: TEXT_DARK,
        overflow: "hidden",
      }}
    >
      {/* Voiceover Track (Edge-TTS Ardi Natural) */}
      <Audio src={staticFile("nixlev_intro_voice.mp3")} />

      {/* BEAT 1: The Creator Dilemma (0 - 160 / 0.0s - 5.3s) */}
      <Sequence from={0} durationInFrames={160}>
        <Beat1Dilemma />
      </Sequence>

      {/* BEAT 2: The Real Pain (160 - 392 / 5.3s - 13.0s) */}
      <Sequence from={160} durationInFrames={232}>
        <Beat2Pain />
      </Sequence>

      {/* BEAT 3: Reveal Nixlev (392 - 450 / 13.0s - 15.0s) */}
      <Sequence from={392} durationInFrames={58}>
        <Beat3Reveal />
      </Sequence>

      {/* BEAT 4: What is Nixlev? (450 - 610 / 15.0s - 20.3s) */}
      <Sequence from={450} durationInFrames={160}>
        <Beat4Overview />
      </Sequence>

      {/* BEAT 5: Product Catalog Showcase (610 - 817 / 20.3s - 27.2s) */}
      <Sequence from={610} durationInFrames={207}>
        <Beat5Products />
      </Sequence>

      {/* BEAT 6: Automated QRIS Checkout (817 - 1129 / 27.2s - 37.6s) */}
      <Sequence from={817} durationInFrames={312}>
        <Beat6QRIS />
      </Sequence>

      {/* BEAT 7: Zero Hassle & Outro CTA (1129 - 1395 / 37.6s - 46.5s) */}
      <Sequence from={1129} durationInFrames={266}>
        <Beat7CTA />
      </Sequence>
    </AbsoluteFill>
  );
};

/* ------------------------------------------------------------------ BEAT 1 */
const Beat1Dilemma: React.FC = () => {
  const frame = useCurrentFrame();
  const scale = interpolate(frame, [0, 24], [1.1, 0.92], {
    easing: Easing.bezier(0.16, 1, 0.3, 1),
    extrapolateRight: "clamp",
  });
  const crossScale = interpolate(frame, [50, 62], [0, 1.15], {
    easing: Easing.bezier(0.16, 1, 0.3, 1),
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        padding: "100px 50px 80px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Audio src={staticFile("whoosh.mp3")} volume={0.4} />
      {frame >= 50 && <Audio src={staticFile("click.mp3")} volume={0.8} />}

      {/* Top Tag */}
      <div
        style={{
          background: "#EAE6DE",
          padding: "10px 24px",
          borderRadius: 999,
          fontSize: 20,
          fontWeight: 800,
          color: NIXLEV_GREEN,
          letterSpacing: 2,
        }}
      >
        DILEMA KREATOR
      </div>

      {/* Phone Profile Mockup */}
      <div
        style={{
          position: "relative",
          transform: `scale(${scale})`,
          width: 580,
          backgroundColor: "#FFFFFF",
          borderRadius: 44,
          border: "8px solid #2B2927",
          boxShadow: "0 30px 70px rgba(0,0,0,0.14)",
          padding: "50px 40px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 20,
        }}
      >
        {/* Lucide User Avatar */}
        <div
          style={{
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #1B3B2B, #3B6B4F)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 10px 25px rgba(27,59,43,0.25)",
          }}
        >
          <User size={58} color="#FFFFFF" strokeWidth={2.2} />
        </div>

        <div style={{ fontSize: 28, fontWeight: 900, color: TEXT_DARK }}>
          @kreator_digital
        </div>
        <div
          style={{
            fontSize: 20,
            color: TEXT_MUTED,
            textAlign: "center",
            lineHeight: 1.4,
          }}
        >
          Ebook, Kelas, & Jasa Freelance <br />
          Cek link penawaran di bio 👇
        </div>

        {/* The Question Mark Link Button */}
        <div
          style={{
            width: "100%",
            marginTop: 18,
            padding: "24px",
            backgroundColor: "#F7F5F0",
            border: "2px dashed #C2BBA8",
            borderRadius: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            fontSize: 24,
            fontWeight: 800,
            color: "#8C8578",
          }}
        >
          <HelpCircle size={28} color={NIXLEV_GOLD} strokeWidth={2.4} />
          <span>Mau taruh link apa disini?</span>
        </div>

        {/* Big Red Ban Stamp */}
        {frame >= 50 && (
          <div
            style={{
              position: "absolute",
              top: "40%",
              left: "6%",
              width: "88%",
              transform: `scale(${crossScale}) rotate(-10deg)`,
              backgroundColor: "#DC2626",
              color: "#FFFFFF",
              padding: "18px 24px",
              borderRadius: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 14,
              fontSize: 36,
              fontWeight: 900,
              boxShadow: "0 20px 45px rgba(220,38,38,0.45)",
              letterSpacing: 1.5,
              zIndex: 30,
            }}
          >
            <XCircle size={38} color="#FFFFFF" strokeWidth={2.8} />
            <span>STOP PAKAI INI!</span>
          </div>
        )}
      </div>

      {/* Subtitle Card */}
      <div
        style={{
          backgroundColor: "#FFFFFF",
          padding: "22px 36px",
          borderRadius: 22,
          boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
          fontSize: 30,
          fontWeight: 800,
          textAlign: "center",
          maxWidth: 900,
        }}
      >
        Mau jualan produk digital, tapi bingung mau taruh link apa di bio?
      </div>
    </AbsoluteFill>
  );
};

/* ------------------------------------------------------------------ BEAT 2 */
const Beat2Pain: React.FC = () => {
  const frame = useCurrentFrame();
  const scale = interpolate(frame, [0, 22], [1.12, 0.94], {
    easing: Easing.bezier(0.16, 1, 0.3, 1),
    extrapolateRight: "clamp",
  });

  const isSecondPart = frame >= 120; // 280-160 = 120 frame offset

  return (
    <AbsoluteFill
      style={{
        padding: "100px 50px 80px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Audio src={staticFile("whoosh.mp3")} volume={0.4} />

      <div
        style={{
          background: "#FEE2E2",
          padding: "10px 24px",
          borderRadius: 999,
          fontSize: 20,
          fontWeight: 800,
          color: "#DC2626",
          letterSpacing: 2,
        }}
      >
        MASALAH LINK BIASA
      </div>

      <div
        style={{
          transform: `scale(${scale})`,
          width: 820,
          display: "flex",
          flexDirection: "column",
          gap: 24,
        }}
      >
        {!isSecondPart ? (
          /* Part A: Tombol Kaku Ngebosenin */
          <div
            style={{
              backgroundColor: "#FFFFFF",
              borderRadius: 36,
              padding: "50px 44px",
              border: "2px solid #E5E0D5",
              boxShadow: "0 25px 60px rgba(0,0,0,0.08)",
              display: "flex",
              flexDirection: "column",
              gap: 20,
            }}
          >
            <div
              style={{
                fontSize: 24,
                fontWeight: 800,
                color: "#DC2626",
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <X size={28} color="#DC2626" strokeWidth={3} />
              <span>Link Bio Konvensional</span>
            </div>
            <div
              style={{
                padding: "22px",
                backgroundColor: "#F3EFE6",
                borderRadius: 16,
                fontSize: 22,
                color: "#8C8578",
                fontWeight: 700,
                textAlign: "center",
                border: "1px solid #E2DDD5",
              }}
            >
              Link Tree Kotak Kaku 1
            </div>
            <div
              style={{
                padding: "22px",
                backgroundColor: "#F3EFE6",
                borderRadius: 16,
                fontSize: 22,
                color: "#8C8578",
                fontWeight: 700,
                textAlign: "center",
                border: "1px solid #E2DDD5",
              }}
            >
              Link Tree Kotak Kaku 2
            </div>
            <div
              style={{
                fontSize: 24,
                color: TEXT_MUTED,
                textAlign: "center",
                marginTop: 10,
                fontWeight: 600,
              }}
            >
              Tampilan flat, tidak ada gambar produk, pembeli langsung pergi.
            </div>
          </div>
        ) : (
          /* Part B: Bikin Website Sendiri Ribet */
          <div
            style={{
              backgroundColor: "#FFFFFF",
              borderRadius: 36,
              padding: "50px 44px",
              border: "2px solid #E5E0D5",
              boxShadow: "0 25px 60px rgba(0,0,0,0.08)",
              display: "flex",
              flexDirection: "column",
              gap: 20,
            }}
          >
            <div
              style={{
                fontSize: 24,
                fontWeight: 800,
                color: "#DC2626",
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <ShieldAlert size={28} color="#DC2626" strokeWidth={2.6} />
              <span>Bikin Website Sendiri?</span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "22px 28px",
                backgroundColor: "#FEE2E2",
                borderRadius: 18,
                color: "#991B1B",
                fontSize: 22,
                fontWeight: 800,
              }}
            >
              <span>Sewa Hosting & Domain</span>
              <span>Rp 800.000 / thn</span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "22px 28px",
                backgroundColor: "#FEE2E2",
                borderRadius: 18,
                color: "#991B1B",
                fontSize: 22,
                fontWeight: 800,
              }}
            >
              <span>Koding WordPress / HTML</span>
              <span>Pusing & Makan Waktu</span>
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          backgroundColor: "#FFFFFF",
          padding: "22px 36px",
          borderRadius: 22,
          boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
          fontSize: 30,
          fontWeight: 800,
          textAlign: "center",
          maxWidth: 900,
        }}
      >
        {!isSecondPart
          ? "Pakai link biasa tampilannya kaku dan orang malas beli."
          : "Bikin website sendiri mahal dan ribet koding."}
      </div>
    </AbsoluteFill>
  );
};

/* ------------------------------------------------------------------ BEAT 3 */
const Beat3Reveal: React.FC = () => {
  const frame = useCurrentFrame();
  const scale = interpolate(frame, [0, 20], [1.18, 0.95], {
    easing: Easing.bezier(0.16, 1, 0.3, 1),
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: NIXLEV_GREEN,
        padding: "100px 50px 80px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        color: "#FFFFFF",
      }}
    >
      <Audio src={staticFile("whoosh.mp3")} volume={0.5} />
      <Audio src={staticFile("click.mp3")} volume={0.8} />

      <div
        style={{
          transform: `scale(${scale})`,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 24,
        }}
      >
        {/* Real Nixlev Logo from project */}
        <div
          style={{
            width: 200,
            height: 200,
            filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.4))",
          }}
        >
          <Img
            src={staticFile("nixlev_logo.png")}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </div>

        <div
          style={{
            fontSize: 28,
            fontWeight: 800,
            color: NIXLEV_GOLD,
            letterSpacing: 4,
            textTransform: "uppercase",
          }}
        >
          Platform Penawaran All-in-One
        </div>
        <h1
          style={{
            fontSize: 130,
            fontWeight: 900,
            letterSpacing: -4,
            margin: 0,
            lineHeight: 0.95,
            color: "#FFFFFF",
          }}
        >
          NIXLEV
        </h1>
        <div
          style={{
            fontSize: 32,
            fontWeight: 600,
            color: "#C2D5C8",
            marginTop: 6,
          }}
        >
          Penawaranmu dalam satu halaman yang jelas.
        </div>
      </div>
    </AbsoluteFill>
  );
};

/* ------------------------------------------------------------------ BEAT 4 */
const Beat4Overview: React.FC = () => {
  const frame = useCurrentFrame();
  const scale = interpolate(frame, [0, 22], [1.1, 0.92], {
    easing: Easing.bezier(0.16, 1, 0.3, 1),
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        padding: "100px 50px 80px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Audio src={staticFile("whoosh.mp3")} volume={0.4} />

      <div
        style={{
          background: "#EAE6DE",
          padding: "10px 24px",
          borderRadius: 999,
          fontSize: 20,
          fontWeight: 800,
          color: NIXLEV_GREEN,
          letterSpacing: 2,
        }}
      >
        APA ITU NIXLEV?
      </div>

      {/* Browser Showcase Mockup */}
      <div
        style={{
          transform: `scale(${scale})`,
          width: 860,
          backgroundColor: "#FFFFFF",
          borderRadius: 32,
          border: "2px solid #E5E0D5",
          boxShadow: "0 30px 70px rgba(0,0,0,0.12)",
          overflow: "hidden",
        }}
      >
        {/* Browser Top Bar */}
        <div
          style={{
            height: 56,
            backgroundColor: "#242220",
            display: "flex",
            alignItems: "center",
            padding: "0 22px",
            gap: 16,
          }}
        >
          <div style={{ display: "flex", gap: 8 }}>
            <div
              style={{
                width: 13,
                height: 13,
                borderRadius: "50%",
                backgroundColor: "#FF5F56",
              }}
            />
            <div
              style={{
                width: 13,
                height: 13,
                borderRadius: "50%",
                backgroundColor: "#FFBD2E",
              }}
            />
            <div
              style={{
                width: 13,
                height: 13,
                borderRadius: "50%",
                backgroundColor: "#27C93F",
              }}
            />
          </div>

          <div
            style={{
              flex: 1,
              backgroundColor: "#3A3734",
              borderRadius: 8,
              padding: "6px 16px",
              color: "#E2DDD5",
              fontSize: 16,
              fontFamily: "monospace",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Globe size={16} color="#D0DDD5" />
            <span>nixlev.com</span>
          </div>
        </div>

        {/* Content Box */}
        <div
          style={{
            padding: "48px 44px",
            display: "flex",
            flexDirection: "column",
            gap: 20,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              backgroundColor: NIXLEV_SAGE,
              padding: "8px 20px",
              borderRadius: 999,
              alignSelf: "flex-start",
            }}
          >
            <Zap size={18} color={NIXLEV_GREEN} />
            <span
              style={{
                fontSize: 18,
                fontWeight: 800,
                color: NIXLEV_GREEN,
                letterSpacing: 1,
              }}
            >
              MICROSITE & LANDING PAGE BUILDER
            </span>
          </div>

          <div
            style={{
              fontSize: 44,
              fontWeight: 900,
              color: TEXT_DARK,
              lineHeight: 1.2,
            }}
          >
            Halaman penawaran elegan yang membuat calon pembeli langsung yakin.
          </div>
          <div
            style={{
              fontSize: 24,
              color: TEXT_MUTED,
              lineHeight: 1.45,
            }}
          >
            Gabungkan bio link, showcase portofolio, dan toko produk digital dalam
            satu halaman bersih tanpa sewa hosting.
          </div>
        </div>
      </div>

      <div
        style={{
          backgroundColor: "#FFFFFF",
          padding: "22px 36px",
          borderRadius: 22,
          boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
          fontSize: 30,
          fontWeight: 800,
          textAlign: "center",
          maxWidth: 900,
        }}
      >
        Nixlev adalah pembuat halaman penawaran dan microsite elegan dalam satu
        halaman yang jelas.
      </div>
    </AbsoluteFill>
  );
};

/* ------------------------------------------------------------------ BEAT 5 */
const Beat5Products: React.FC = () => {
  const frame = useCurrentFrame();
  const scale = interpolate(frame, [0, 22], [1.1, 0.94], {
    easing: Easing.bezier(0.16, 1, 0.3, 1),
    extrapolateRight: "clamp",
  });

  const card1Spring = interpolate(frame, [10, 30], [50, 0], {
    easing: Easing.bezier(0.16, 1, 0.3, 1),
    extrapolateRight: "clamp",
  });
  const card2Spring = interpolate(frame, [50, 70], [50, 0], {
    easing: Easing.bezier(0.16, 1, 0.3, 1),
    extrapolateRight: "clamp",
  });
  const card3Spring = interpolate(frame, [90, 110], [50, 0], {
    easing: Easing.bezier(0.16, 1, 0.3, 1),
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        padding: "100px 50px 80px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Audio src={staticFile("whoosh.mp3")} volume={0.4} />

      <div
        style={{
          background: "#EAE6DE",
          padding: "10px 24px",
          borderRadius: 999,
          fontSize: 20,
          fontWeight: 800,
          color: NIXLEV_GREEN,
          letterSpacing: 2,
        }}
      >
        BISA JUAL APA SAJA?
      </div>

      {/* 3 Staggered Product Cards with Lucide Icons */}
      <div
        style={{
          transform: `scale(${scale})`,
          width: 820,
          display: "flex",
          flexDirection: "column",
          gap: 20,
        }}
      >
        {/* Card 1: Ebook */}
        <div
          style={{
            transform: `translateY(${card1Spring}px)`,
            backgroundColor: "#FFFFFF",
            padding: "26px 32px",
            borderRadius: 24,
            border: "1.5px solid #E5E0D5",
            boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <div
              style={{
                width: 68,
                height: 68,
                borderRadius: 18,
                backgroundColor: NIXLEV_SAGE,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <BookOpen size={36} color={NIXLEV_GREEN} strokeWidth={2.2} />
            </div>
            <div>
              <div style={{ fontSize: 26, fontWeight: 900, color: TEXT_DARK }}>
                Ebook & Panduan PDF
              </div>
              <div style={{ fontSize: 18, color: TEXT_MUTED }}>
                File otomatis terkirim setelah bayar
              </div>
            </div>
          </div>
          <div
            style={{ fontSize: 26, fontWeight: 900, color: NIXLEV_GREEN }}
          >
            Rp 99.000
          </div>
        </div>

        {/* Card 2: Kursus Online */}
        <div
          style={{
            transform: `translateY(${card2Spring}px)`,
            backgroundColor: "#FFFFFF",
            padding: "26px 32px",
            borderRadius: 24,
            border: "1.5px solid #E5E0D5",
            boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <div
              style={{
                width: 68,
                height: 68,
                borderRadius: 18,
                backgroundColor: NIXLEV_SAGE,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <GraduationCap
                size={38}
                color={NIXLEV_GREEN}
                strokeWidth={2.2}
              />
            </div>
            <div>
              <div style={{ fontSize: 26, fontWeight: 900, color: TEXT_DARK }}>
                Kelas Video & Webinar
              </div>
              <div style={{ fontSize: 18, color: TEXT_MUTED }}>
                Akses link materi instan
              </div>
            </div>
          </div>
          <div
            style={{ fontSize: 26, fontWeight: 900, color: NIXLEV_GREEN }}
          >
            Rp 249.000
          </div>
        </div>

        {/* Card 3: Jasa & Konsultasi */}
        <div
          style={{
            transform: `translateY(${card3Spring}px)`,
            backgroundColor: "#FFFFFF",
            padding: "26px 32px",
            borderRadius: 24,
            border: "1.5px solid #E5E0D5",
            boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <div
              style={{
                width: 68,
                height: 68,
                borderRadius: 18,
                backgroundColor: NIXLEV_SAGE,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Briefcase size={36} color={NIXLEV_GREEN} strokeWidth={2.2} />
            </div>
            <div>
              <div style={{ fontSize: 26, fontWeight: 900, color: TEXT_DARK }}>
                Jasa Desain & Konsultasi
              </div>
              <div style={{ fontSize: 18, color: TEXT_MUTED }}>
                Booking jadwal & brief otomatis
              </div>
            </div>
          </div>
          <div
            style={{ fontSize: 26, fontWeight: 900, color: NIXLEV_GREEN }}
          >
            Rp 500.000
          </div>
        </div>
      </div>

      <div
        style={{
          backgroundColor: "#FFFFFF",
          padding: "22px 36px",
          borderRadius: 22,
          boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
          fontSize: 30,
          fontWeight: 800,
          textAlign: "center",
          maxWidth: 900,
        }}
      >
        Kamu bisa pajang profil, portofolio, sampai jualan ebook, kelas online,
        dan jasa kamu.
      </div>
    </AbsoluteFill>
  );
};

/* ------------------------------------------------------------------ BEAT 6 */
const Beat6QRIS: React.FC = () => {
  const frame = useCurrentFrame();
  const scale = interpolate(frame, [0, 22], [1.12, 0.92], {
    easing: Easing.bezier(0.16, 1, 0.3, 1),
    extrapolateRight: "clamp",
  });
  const notifSlide = interpolate(frame, [80, 105], [-50, 0], {
    easing: Easing.bezier(0.16, 1, 0.3, 1),
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        padding: "100px 50px 80px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Audio src={staticFile("whoosh.mp3")} volume={0.4} />
      {frame >= 80 && <Audio src={staticFile("click.mp3")} volume={0.8} />}

      <div
        style={{
          background: "#EAE6DE",
          padding: "10px 24px",
          borderRadius: 999,
          fontSize: 20,
          fontWeight: 800,
          color: NIXLEV_GREEN,
          letterSpacing: 2,
        }}
      >
        CHECKOUT QRIS OTOMATIS
      </div>

      {/* QRIS Modal Card */}
      <div
        style={{
          transform: `scale(${scale})`,
          width: 780,
          backgroundColor: "#FFFFFF",
          borderRadius: 36,
          padding: "44px",
          border: "2px solid #E5E0D5",
          boxShadow: "0 25px 60px rgba(0,0,0,0.12)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 22,
        }}
      >
        {/* QRIS Badge */}
        <div
          style={{
            backgroundColor: "#B91C1C",
            color: "#FFFFFF",
            padding: "8px 26px",
            borderRadius: 12,
            fontSize: 24,
            fontWeight: 900,
            letterSpacing: 2,
          }}
        >
          QRIS RESMI
        </div>

        {/* Clean Vector QR Code Container */}
        <div
          style={{
            width: 250,
            height: 250,
            backgroundColor: "#F7F5F0",
            border: "2px solid #CBD5E1",
            borderRadius: 24,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            boxShadow: "inset 0 2px 6px rgba(0,0,0,0.04)",
          }}
        >
          <QrCode size={150} color="#1B3B2B" strokeWidth={1.8} />
          <div
            style={{
              fontSize: 15,
              fontWeight: 800,
              color: TEXT_MUTED,
              letterSpacing: 1,
            }}
          >
            SCAN SEMUA BANK & E-WALLET
          </div>
        </div>

        {/* Bank Partner Logos */}
        <div
          style={{
            fontSize: 20,
            fontWeight: 800,
            color: TEXT_MUTED,
            letterSpacing: 1,
          }}
        >
          BCA • MANDIRI • BRI • BNI • GOPAY • DANA • OVO
        </div>

        {/* Success Slide Notification */}
        {frame >= 80 && (
          <div
            style={{
              transform: `translateY(${notifSlide}px)`,
              backgroundColor: NIXLEV_GREEN,
              color: "#FFFFFF",
              width: "100%",
              padding: "20px 26px",
              borderRadius: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              boxShadow: "0 15px 35px rgba(27,59,43,0.35)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <CheckCircle2 size={34} color={NIXLEV_GOLD} strokeWidth={2.4} />
              <div>
                <div style={{ fontSize: 22, fontWeight: 800 }}>
                  Pembayaran Berhasil!
                </div>
                <div style={{ fontSize: 16, color: "#C2D5C8" }}>
                  Akses produk langsung terkirim ke email pembeli
                </div>
              </div>
            </div>
            <span
              style={{
                fontSize: 18,
                fontWeight: 900,
                color: NIXLEV_GOLD,
              }}
            >
              OTOMATIS
            </span>
          </div>
        )}
      </div>

      <div
        style={{
          backgroundColor: "#FFFFFF",
          padding: "22px 36px",
          borderRadius: 22,
          boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
          fontSize: 30,
          fontWeight: 800,
          textAlign: "center",
          maxWidth: 900,
        }}
      >
        Pembeli tinggal scan, pesanan langsung beres tanpa cek bukti transfer
        manual.
      </div>
    </AbsoluteFill>
  );
};

/* ------------------------------------------------------------------ BEAT 7 */
const Beat7CTA: React.FC = () => {
  const frame = useCurrentFrame();
  const scale = interpolate(frame, [0, 22], [1.12, 0.92], {
    easing: Easing.bezier(0.16, 1, 0.3, 1),
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        padding: "100px 50px 80px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Audio src={staticFile("whoosh.mp3")} volume={0.4} />
      <Sequence from={24} layout="none">
        <Audio src={staticFile("click.mp3")} volume={0.7} />
      </Sequence>

      <div
        style={{
          background: "#EAE6DE",
          padding: "10px 24px",
          borderRadius: 999,
          fontSize: 20,
          fontWeight: 800,
          color: NIXLEV_GREEN,
          letterSpacing: 2,
        }}
      >
        5 MENIT LANGSUNG JUALAN
      </div>

      {/* Outro Hero Card */}
      <div
        style={{
          transform: `scale(${scale})`,
          width: 820,
          backgroundColor: "#FFFFFF",
          borderRadius: 40,
          padding: "50px 50px",
          border: `2px solid ${BORDER_COLOR}`,
          boxShadow: "0 30px 70px rgba(0,0,0,0.12)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 26,
          textAlign: "center",
        }}
      >
        {/* Real Nixlev Logo from Project */}
        <div style={{ width: 140, height: 140 }}>
          <Img
            src={staticFile("nixlev_logo.png")}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </div>

        <h2
          style={{
            fontSize: 74,
            fontWeight: 900,
            color: NIXLEV_GREEN,
            margin: 0,
            letterSpacing: -3,
            lineHeight: 1,
          }}
        >
          NIXLEV.COM
        </h2>

        <p
          style={{
            fontSize: 26,
            color: TEXT_MUTED,
            margin: 0,
            maxWidth: 620,
            lineHeight: 1.45,
          }}
        >
          Buat halaman penawaran produk digital & jasamu sekarang. Tanpa sewa hosting,
          tanpa koding, langsung terima QRIS.
        </p>

        {/* Big Forest Green Button with ArrowRight */}
        <div
          style={{
            backgroundColor: NIXLEV_GREEN,
            color: "#FFFFFF",
            width: "100%",
            padding: "24px",
            borderRadius: 22,
            fontSize: 28,
            fontWeight: 800,
            boxShadow: "0 15px 35px rgba(27,59,43,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 14,
          }}
        >
          <span>Mulai Buat Halaman Gratis</span>
          <ArrowRight size={30} color="#FFFFFF" strokeWidth={2.8} />
        </div>
      </div>

      <div
        style={{
          backgroundColor: "#FFFFFF",
          padding: "22px 36px",
          borderRadius: 22,
          boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
          fontSize: 30,
          fontWeight: 800,
          textAlign: "center",
          maxWidth: 900,
        }}
      >
        Bikin halamanmu sekarang di Nixlev dot com.
      </div>
    </AbsoluteFill>
  );
};
